/**
 * Content-Security-Policy helpers. The shells carry a fallback meta policy with a nonce
 * placeholder; production CSP is set by Caddy. After the launch exchange the app appends a
 * second meta policy whose `connect-src` is exactly the origins in the launch response
 * (policies combine restrictively, so this only ever tightens).
 */
import type { LaunchResponse } from '@ccphone/contracts';

export interface CspInput {
  nonce: string;
  connectOrigins: readonly string[];
}

export function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/** api, relay and SIP origins from a launch response, deduplicated, in a stable order */
export function connectOriginsFromLaunch(b: Pick<LaunchResponse, 'api_base' | 'relay' | 'sip'>): string[] {
  const out: string[] = [];
  for (const u of [b.api_base, b.relay.url, b.sip.wss]) {
    const o = originOf(u);
    if (o && !out.includes(o)) out.push(o);
  }
  return out;
}

export function buildCsp(i: CspInput): string {
  const connect = ["'self'", ...i.connectOrigins].join(' ');
  return [
    "default-src 'none'",
    `script-src 'nonce-${i.nonce}'`,
    `style-src 'self' 'nonce-${i.nonce}'`,
    "img-src 'self' data:",
    "font-src 'self'",
    "media-src 'self' blob:",
    "manifest-src 'self'",
    "worker-src 'self'",
    `connect-src ${connect}`,
    "base-uri 'none'",
    "form-action 'none'",
    "object-src 'none'",
  ].join('; ');
}

/** the nonce Caddy (or the placeholder in dev) put on the shell's scripts */
export function currentNonce(doc: Document = document): string {
  const meta = doc.querySelector('meta[property="csp-nonce"]');
  return meta?.getAttribute('nonce') ?? '__CC_NONCE__';
}

/** appends the runtime policy; idempotent per document */
export function applyRuntimeCsp(b: Pick<LaunchResponse, 'api_base' | 'relay' | 'sip'>, doc: Document = document): string {
  const policy = buildCsp({ nonce: currentNonce(doc), connectOrigins: connectOriginsFromLaunch(b) });
  if (doc.querySelector('meta[data-ccphone-runtime-csp]')) return policy;
  const meta = doc.createElement('meta');
  meta.setAttribute('http-equiv', 'Content-Security-Policy');
  meta.setAttribute('content', policy);
  meta.setAttribute('data-ccphone-runtime-csp', '1');
  doc.head.appendChild(meta);
  return policy;
}
